import React, { useContext, useRef, useState } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import {
  PlayCircleOutline,
  Download,
  FolderZip,
  PlaylistAdd,
  Delete,
} from '@mui/icons-material';
import DialogsContext from 'app/contexts/DialogsContext';
import { useFilePlayerContext } from '../FilePlayerContext';
import { useZipDownload } from '../hooks/useZipDownload';
import { FileConfirmDeleteDialog } from './FileConfirmDeleteDialog';
import { AddToPlaylistDialog } from './AddToPlaylistDialog';
import { FileListStateShape } from '../hooks/useFileListState';

export const FileActionBar = ({ fileListState }) => {
  const {
    playItems,
    deleteFilesMutation,
    markFilesDeleted,
  } = useFilePlayerContext();
  const [deleteFiles] = deleteFilesMutation;

  const {
    selectedCount = 0,
    clearAll,
    checkedItems,
    selectedPurchasedItemIds,
    selectedItems,
  } = fileListState;

  const { openDialog } = useContext(DialogsContext);
  const { startZipDownload } = useZipDownload();
  const [showPlaylistDialog, setShowPlaylistDialog] = useState(false);
  const downloadLinkRef = useRef(null);

  const handleZip = () => {
    startZipDownload({ checkedItems });
    clearAll();
  };

  const handlePlay = () => {
    playItems(selectedItems);
  };

  const handleDeleteConfirm = () => {
    const idsToDelete = [...selectedPurchasedItemIds];
    markFilesDeleted(idsToDelete);
    deleteFiles({ ids: idsToDelete, permanent: 'false' });
    clearAll();
  };

  const handleDelete = (evt) => {
    openDialog({
      component: FileConfirmDeleteDialog,
      props: {
        onConfirm: handleDeleteConfirm,
      },
    });

    evt.preventDefault();
  };

  const handleDownload = () => {
    const file = selectedItems[0];
    const fixedUrl = file.original_url.replace(/^http:\/\//, 'https://');
    const encodedFilename = encodeURIComponent(file.fileName);

    downloadLinkRef.current.href = `${fixedUrl}?download=1&filename=${encodedFilename}`;
    downloadLinkRef.current.click();
  };

  if (selectedCount === 0) return null;

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: 'grey.200',
          padding: 1,
          borderRadius: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton onClick={clearAll}>
            <span>&lt;</span>
          </IconButton>
          <Typography variant="body1" sx={{ ml: 1 }}>
            {`${selectedCount} selected`}
          </Typography>
        </Box>

        <Box>
          <IconButton onClick={handlePlay}>
            <PlayCircleOutline />
          </IconButton>
          {selectedCount === 1 && (
            <IconButton onClick={handleDownload}>
              <Download />
            </IconButton>
          )}
          {selectedCount > 1 && (
            <IconButton onClick={handleZip}>
              <FolderZip />
            </IconButton>
          )}
          <IconButton onClick={() => setShowPlaylistDialog(true)}>
            <PlaylistAdd />
          </IconButton>
          <IconButton onClick={handleDelete}>
            <Delete />
          </IconButton>
        </Box>
      </Box>

      <AddToPlaylistDialog
        open={showPlaylistDialog}
        onClose={() => setShowPlaylistDialog(false)}
        purchasedItemIds={selectedPurchasedItemIds}
        clearAll={clearAll}
      />

      {/* eslint-disable-next-line jsx-a11y/anchor-has-content */}
      <a
        ref={downloadLinkRef}
        style={{ display: 'none' }}
        target="_blank"
        rel="noopener noreferrer"
        aria-hidden="true"
      />
    </>
  );
};

FileActionBar.propTypes = {
  fileListState: FileListStateShape.isRequired,
};
